import { DefaultLayout, VerticalLayout } from 'layouts';
import React, { Suspense } from 'react';
import { useRoutes } from 'react-router-dom';
import PrivateRoute from './PrivateRoute';
import Root from './Root';

const Maintenance = React.lazy(() => import('pages/other/Maintenance'));
const ServerError = React.lazy(() => import('pages/error/ServerError'));
const ErrorPageNotFound = React.lazy(() => import('pages/error/PageNotFound'));
const Logout = React.lazy(() => import('pages/account/Logout'));

//superAdmin route Settings pages
const AamsLogin = React.lazy(() => import('pages/aams-login'));
const Pdf = React.lazy(() => import('pages/aams-pdf'));
const TreeViewOptimise = React.lazy(() => import('pages/aams-treeview/index'));
const Schedule = React.lazy(() => import('pages/aams-schedule/index'));
const ScheduleVisualisation = React.lazy(() => import('pages/aams-schedule-visualization/index'));
const DataVisualization = React.lazy(() => import('pages/aams-data-visualization/index'));
const Settings = React.lazy(() => import('pages/aams-superadmin-settings/SettingsLayout'));
const OilAnalysis = React.lazy(() => import('pages/aams-oil-analysis/'));
const OilAnalysisPdf = React.lazy(() => import('pages/aams-oilanalysisPDF'));
const NablPdf = React.lazy(() => import('pages/aams-oilanalysisPDF/NABL/index'));
const SensorMapping = React.lazy(() => import('pages/aams-superadmin-settings/Sensors/index'));
const MultiChannelMapping = React.lazy(() => import('pages/aams-superadmin-settings/MultiChannel/index'));
const UserManagement = React.lazy(() => import('pages/aams-superadmin-settings/User/index'));

//CustomerAdmin route Settings pages
const CustomerTreeViewOptimise = React.lazy(() => import('pages/aams-customer-treeview/index'));
const CustomerAdminSchedule = React.lazy(() => import('pages/aams-customer-schedule/index'));
const CustomerAdminscheduleVisualisation = React.lazy(() => import('pages/aams-customer-schedule-visualization/index'));
const CustomerAdminDataVisualization = React.lazy(() => import('pages/aams-customer-data-visualization'));

const loading = () => <div className=""></div>;

type LoadComponentProps = {
    component: React.LazyExoticComponent<() => JSX.Element>;
};

const LoadComponent = ({ component: Component }: LoadComponentProps) => (
    <Suspense fallback={loading()}>
        <Component />
    </Suspense>
);

const AllRoutes = () => {
    const Layout = VerticalLayout;

    return useRoutes([
        { path: '/', element: <Root /> },
        {
            path: '/aams-login',
            element: <LoadComponent component={AamsLogin} />,
        },
        {
            path: '/pdf',
            element: <LoadComponent component={Pdf} />,
        },
        {
            path: '/oil-analysis-pdf',
            element: <LoadComponent component={OilAnalysisPdf} />,
        },
        {
            path: 'nabl',
            element: <LoadComponent component={NablPdf} />,
        },
        {
            // public routes
            path: '/',
            element: <DefaultLayout />,
            children: [
                {
                    path: 'account',
                    children: [{ path: 'logout', element: <LoadComponent component={Logout} /> }],
                },
                {
                    path: 'error-404',
                    element: <LoadComponent component={ErrorPageNotFound} />,
                },
                {
                    path: 'error-500',
                    element: <LoadComponent component={ServerError} />,
                },
                {
                    path: 'maintenance',
                    element: <LoadComponent component={Maintenance} />,
                },
            ],
        },
        {
            path: '/',
            element: <PrivateRoute roles={['ADMIN', 'ANALYST']} component={Layout} />,
            children: [
                {
                    path: 'settings',
                    element: <LoadComponent component={TreeViewOptimise} />,
                },
                {
                    path: 'schedule',
                    element: <LoadComponent component={Schedule} />,
                },
                {
                    path: 'schedule-view',
                    element: <LoadComponent component={ScheduleVisualisation} />,
                },
                {
                    path: 'data-view',
                    element: <LoadComponent component={DataVisualization} />,
                },
                {
                    path: 'admin-settings',
                    element: <LoadComponent component={Settings} />,
                },
                {
                    path: 'sensor',
                    element: <LoadComponent component={SensorMapping} />,
                },
                {
                    path: 'multi-channel',
                    element: <LoadComponent component={MultiChannelMapping} />,
                },
                {
                    path: 'user',
                    element: <LoadComponent component={UserManagement} />,
                },
            ],
        },
        {
            path: '/',
            element: <PrivateRoute roles={['ADMIN', 'CUSTOMER_ADMIN']} component={Layout} />,
            children: [
                {
                    path: 'customer-admin-route',
                    element: <LoadComponent component={CustomerTreeViewOptimise} />,
                },
                {
                    path: 'customer-schedule',
                    element: <LoadComponent component={CustomerAdminSchedule} />,
                },
                {
                    path: 'customer-schedule-view',
                    element: <LoadComponent component={CustomerAdminscheduleVisualisation} />,
                },
                {
                    path: 'customer-data-view',
                    element: <LoadComponent component={CustomerAdminDataVisualization} />,
                },
                {
                    path: 'oil-analysis',
                    element: <LoadComponent component={OilAnalysis} />,
                },
            ],
        },
        {
            path: '/employee',
            element: <PrivateRoute roles={['EMPLOYEE', 'ADMIN', 'MANAGER']} component={Layout} />,
            children: [
                {
                    path: 'schedule',
                    element: <LoadComponent component={CustomerAdminSchedule} />,
                },
            ],
        },
    ]);
};

export { AllRoutes };
